import { ReactNode, useMemo, useState } from 'react';

import { PortalType } from '../../config/enum';

import PortalContext from './PortalContext';

/**
 * Get tenant from hostname
 *
 * Example:
 *
 * partner.energic.sa => energic
 * runa.partner.energic.sa => energic
 */
const getTenant = (): string => {
  const { hostname } = window.location;

  const parts = hostname.toLowerCase().split('.');

  // Handle localhost
  if (parts.length < 2 || (parts.length === 2 && parts[1] === 'localhost')) {
    return '';
  }

  /**
   * Main domain name
   *
   * energic.sa => energic
   */
  return parts[parts.length - 2] || '';
};

/**
 * Detect the portal type based on the hostname
 * @returns {string} portalType
 */
export const detectPortalType = (): string => {
  const host = window.location.hostname.toLowerCase();
  if (host.includes(PortalType.NETWORKS)) return PortalType.NETWORKS;
  if (host.includes(PortalType.PARTNER)) return PortalType.PARTNER;
  if (host.includes(PortalType.MOBILITY)) return PortalType.MOBILITY;
  if (host.includes(PortalType.CHARGER)) return PortalType.CHARGER;
  if (host.includes(PortalType.APP)) return PortalType.APP;
  return PortalType.APP;
};

/**
 * Detect the portal type based on the hostname
 * @returns {string} portalType
 */
export const detectPortalApiType = (): string => {
  const host = window.location.hostname.toLowerCase();

  if (host.includes(PortalType.NETWORKS)) return PortalType.NETWORKS;
  if (host.includes(PortalType.PARTNER)) return PortalType.PARTNER;
  if (host.includes(PortalType.MOBILITY)) return PortalType.MOBILITY;
  if (host.includes(PortalType.CHARGER)) return PortalType.CHARGER;
  if (host.includes(PortalType.APP)) return 'common';
  return PortalType.APP;
};

interface PortalProviderProperties {
  children: ReactNode;
}

/**
 * Portal Provider
 * @param {any} param0 - The param0
 * @param {any} param0.children - The children prop
 */
export function PortalProvider({ children }: PortalProviderProperties) {
  const portalType = detectPortalType();
  const tenant = getTenant();
  const apiType = detectPortalApiType();

  const [activePortal, setActivePortalState] = useState<string>(() => {
    return localStorage.getItem('activePortal') || PortalType.NETWORKS;
  });
  /**
   * Set Active Portal
   * @param {any} portal - The portal
   */
  const setActivePortal = (portal: string) => {
    setActivePortalState(portal);
    localStorage.setItem('activePortal', portal);
  };

  /**
   * Memoized Context Value
   */
  const contextValue = useMemo(
    () => ({
      portalType,
      tenant,
      apiType,
      activePortal,
      setActivePortal,
    }),
    [portalType, tenant, apiType, activePortal],
  );

  return <PortalContext.Provider value={contextValue}>{children}</PortalContext.Provider>;
}

export default PortalProvider;
